import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrencyBRL = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatCurrencyUSD = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

const traduzirMes = (mes) => {
  const meses = {
    'January': 'Janeiro',
    'February': 'Fevereiro',
    'March': 'Março',
    'April': 'Abril',
    'May': 'Maio',
    'June': 'Junho',
    'July': 'Julho',
    'August': 'Agosto',
    'September': 'Setembro',
    'October': 'Outubro',
    'November': 'Novembro',
    'December': 'Dezembro'
  };
  return meses[mes] || mes;
};

function DashboardProventos({ getAuthHeaders }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const response = await axios.get(`${API}/dashboard-proventos/estatisticas`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard Proventos</h1>

      {/* Seção 1: Proventos em Reais */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="mr-2">🇧🇷</span> Proventos em Reais
        </h2>

        {/* Primeira Linha - 2 Cards Principais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Card Mês Atual */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Proventos do Mês Atual</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrencyBRL(stats.real.mes_atual)}</p>
              </div>
            </div>
          </div>

          {/* Card Média 12 Meses */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Média — Últimos 12 Meses</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrencyBRL(stats.real.media_12_meses)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Segunda Linha - 3 Cards Últimos 3 Meses */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.real.ultimos_3_meses.map((mes, index) => (
            <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 mb-2">
                  {traduzirMes(mes.mes)} {mes.ano}
                </p>
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  {formatCurrencyBRL(mes.valor)}
                </p>
                <div className={`inline-flex items-center text-sm font-medium ${
                  mes.variacao >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {mes.variacao >= 0 ? (
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  )}
                  {mes.variacao >= 0 ? '+' : ''}{mes.variacao}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Seção 2: Proventos em Dólares */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="mr-2">🇺🇸</span> Proventos em Dólares
        </h2>

        {/* Primeira Linha - 2 Cards Principais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Card Mês Atual */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-emerald-100">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Proventos do Mês Atual</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrencyUSD(stats.dolar.mes_atual)}</p>
              </div>
            </div>
          </div>

          {/* Card Média 12 Meses */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-cyan-100">
                <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Média — Últimos 12 Meses</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrencyUSD(stats.dolar.media_12_meses)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Segunda Linha - 3 Cards Últimos 3 Meses */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.dolar.ultimos_3_meses.map((mes, index) => (
            <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 mb-2">
                  {traduzirMes(mes.mes)} {mes.ano}
                </p>
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  {formatCurrencyUSD(mes.valor)}
                </p>
                <div className={`inline-flex items-center text-sm font-medium ${
                  mes.variacao >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {mes.variacao >= 0 ? (
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  )}
                  {mes.variacao >= 0 ? '+' : ''}{mes.variacao}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DashboardProventos;
